import React, { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { IconArrowLeft, IconMapPin } from "@tabler/icons-react"
import type { ILocation } from "@dc-ireland/shared"
import apiClient from "../../../lib/api"
import { CHAPTER_REGISTRY, chapterPath, getChapterMeta } from "../../../lib/chapters"
import LandingNav from "../LandingNav"
import LandingFooter from "../LandingFooter"

type Props = {
  slug: string
}

const ChapterComingSoonPage: React.FC<Props> = ({ slug }) => {
  const meta = getChapterMeta(slug)
  const [location, setLocation] = useState<ILocation | null>(null)

  useEffect(() => {
    apiClient
      .getLocations()
      .then((locations) => setLocation(locations.find((l) => l.slug === slug) ?? null))
      .catch(() => setLocation(null))
  }, [slug])

  if (!meta) return null

  const otherChapters = CHAPTER_REGISTRY.filter((c) => c.slug !== slug)
  const notifyHref = `mailto:info@dominioncity.ie?subject=${encodeURIComponent(`${meta.name} chapter — keep me updated`)}`
  const address =
    location && location.address.length > 0 && location.address[0] !== "Coming soon"
      ? location.address.join(" · ")
      : null

  return (
    <div className="min-h-screen bg-[#0a0a12]">
      <LandingNav />

      <section className="flex min-h-[calc(100vh-80px)] flex-col items-center justify-center px-4 pb-20 pt-28 md:px-8">
        <div className="flex max-w-2xl flex-col items-center text-center">
          <div className="mb-8 flex h-16 w-16 items-center justify-center rounded-2xl border border-[#9e7e43]/30 bg-[#9e7e43]/10">
            <IconMapPin size={28} className="text-[#9e7e43]" stroke={1.5} />
          </div>

          <p className="font-dm-sans text-xs font-semibold uppercase tracking-[0.28em] text-[#9e7e43]">
            Dominion City Ireland
          </p>

          <h1 className="mt-4 font-outfit text-5xl font-semibold tracking-tight text-white md:text-7xl">
            {meta.name}
          </h1>

          <p className="mt-8 font-outfit text-2xl font-light tracking-[0.2em] text-white/90 md:text-3xl">
            Coming Soon
          </p>

          <p className="mt-6 max-w-md font-dm-sans text-base leading-7 text-white/60">
            {meta.tagline}
          </p>

          {address && (
            <p className="mt-3 font-dm-sans text-sm text-white/40">{address}</p>
          )}

          <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-5 py-2.5 font-dm-sans text-sm font-medium text-white transition hover:border-white/30 hover:bg-white/10"
            >
              <IconArrowLeft size={16} />
              Back to Home
            </Link>
            <a
              href={notifyHref}
              className="inline-flex items-center rounded-full bg-[#9e7e43] px-5 py-2.5 font-dm-sans text-sm font-semibold text-white transition hover:bg-[#b8954f]"
            >
              Notify Me
            </a>
          </div>
        </div>

        <div className="mt-20 w-full max-w-3xl">
          <p className="mb-4 text-center font-dm-sans text-xs font-semibold uppercase tracking-[0.22em] text-white/40">
            Explore other chapters
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            {otherChapters.map((chapter) => (
              <Link
                key={chapter.slug}
                to={chapterPath(chapter.slug)}
                className={`inline-block rounded-full border px-4 py-1.5 font-dm-sans text-xs font-medium transition ${
                  chapter.status === "live"
                    ? "border-[#9e7e43]/40 text-[#9e7e43] hover:border-[#9e7e43] hover:bg-[#9e7e43]/10"
                    : "border-white/10 text-white/50 hover:border-white/25 hover:text-white/80"
                }`}
              >
                {chapter.name}
                {chapter.status === "coming-soon" && (
                  <span className="ml-1.5 text-[10px] uppercase tracking-wider opacity-60">Soon</span>
                )}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <LandingFooter />
    </div>
  )
}

export default ChapterComingSoonPage
